import React, { useMemo } from 'react';
import Decimal from 'decimal.js';
import { Trade, TokenInfo } from '../types';
import { formatPrice, formatQuantity, formatTime, solscanUrl } from '../utils/formatting';
import { OpenLink } from './OpenLink';
import './TradeHistory.css';

interface TradeHistoryProps {
    trades: Trade[];
    asset: TokenInfo;
    maxTrades?: number;
    showFailedTrades: boolean;
    setShowFailedTrades: (v: boolean) => void;
}

// Memoized individual trade row component
const TradeRow = React.memo<{
    trade: Trade,
    maxQuantity: Decimal,
    referencePrice: Decimal
}>(({ trade, maxQuantity, referencePrice }) => {
    const formattedPrice = useMemo(() => formatPrice(trade.price, referencePrice), [trade.price, referencePrice]);
    const formattedQuantity = useMemo(() => formatQuantity(trade.quantity), [trade.quantity]);
    const formattedTime = useMemo(() => formatTime(trade.timestamp), [trade.timestamp]);

    // Calculate size bar width (0-100%)
    const sizePercentage = trade.quantity.div(maxQuantity).mul(100).toNumber();

    return (
        <div className={`trade-row ${trade.side}`}>
            <span className="price">{formattedPrice}</span>
            <span className="failed">{!trade.success && "⛔"}</span>
            <div className="size-visualization">
                <div
                    className={`size-bar ${trade.side}`}
                    style={{ width: `${sizePercentage}%` }}
                />
                <span className="size-value">{formattedQuantity}</span>
            </div>
            <span className="pair">{trade.baseSymbol}/{trade.quoteSymbol}</span>
            <span className="time">{formattedTime}</span>
	    <a className="hash open-new-window-icon" href={solscanUrl({tx: trade.signature})} target="_blank">
		{trade.signature.slice(0, 5)}...
		<OpenLink />
	    </a>
        </div>
    );
});

const ShowFailedButton = React.memo<{
    show: boolean,
    set: (v: boolean) => void
}>(({ show, set }) => {
    return (
	<div className={`toggle-button ${show ? "enabled" : "disabled"}`} onClick={() => set(!show)}>
	    Show Failed
	</div>
    );
});

const TradeHistory = React.memo<TradeHistoryProps>(({
    trades,
    asset,
    maxTrades = 50,
    showFailedTrades,
    setShowFailedTrades
}) => {
    // Memoize filtered and processed trades
    const { displayTrades, maxQuantity, referencePrice } = useMemo(() => {
        const displayTrades = (showFailedTrades ? trades : trades.filter(t => t.success)).slice(0, maxTrades);

        // Calculate max quantity for size bar scaling
        const maxQuantity = displayTrades.length > 0
            ? displayTrades.reduce((max, trade) => trade.quantity.gt(max) ? trade.quantity : max, new Decimal(0))
            : new Decimal(1);

        // Use median price as reference for consistent formatting
        const referencePrice = displayTrades.length > 0
            ? displayTrades.reduce((sum, trade) => sum.add(trade.price), new Decimal(0)).div(displayTrades.length)
            : new Decimal(1);

        return { displayTrades, maxQuantity, referencePrice };
    }, [trades, asset.symbol, maxTrades, showFailedTrades]);

    return (
        <div className="trade-history">
            <div className="trade-history-header">
                <h2>Recent Trades: {asset.symbol}</h2>
		<ShowFailedButton show={showFailedTrades} set={setShowFailedTrades}/>
            </div>

            <div className="trades-container">
                <div className="trades-header">
                    <span>Price</span>
                    <span/>
                    <span>Quantity</span>
                    <span>Pair</span>
                    <span>Time</span>
                    <span>Hash</span>
                </div>

                <div className="trades-list">
                    {displayTrades.length === 0 ? (
                        <div className="no-trades">
                            <p>No trades yet. Waiting for live data...</p>
                        </div>
                    ) : (
                        displayTrades.map((trade) => (
                            <TradeRow
                                key={trade.id}
                                trade={trade}
                                maxQuantity={maxQuantity}
                                referencePrice={referencePrice}
                            />
                        ))
                    )}
                </div>
            </div>

        </div>
    );
});

export default TradeHistory; 
